/**
 * @fileoverview Login Page
 *
 * Purpose: Entry point for user authentication using WorkOS hosted UI.
 * URL: /login
 *
 * Flow:
 * - If already authenticated, returns to `?redirect=` (a validated in-app path
 *   carried from the auth guard, so a reloaded deep link survives), else the
 *   dashboard — or onboarding if needed
 * - Otherwise shows Sign In / Sign Up buttons that redirect to WorkOS hosted UI
 * - WorkOS handles all auth methods: Email+Password, Magic Link, Google, Microsoft
 *
 * Dependencies:
 * - @workos-inc/authkit-react (useAuth for signIn/signUp)
 * - convex/react (useConvexAuth for auth state)
 * - Convex: users.queries.getCurrentUser
 */

import {
  createFileRoute,
  Link,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useConvexAuth } from "convex/react";
import { useAuth } from "@workos-inc/authkit-react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";

import { api } from "@cvx/_generated/api";
import { Button } from "@/ui/primitives";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { Route as OnboardingOrgRoute } from "@/routes/_app/_auth/onboarding/_layout.organization";
import { Route as DashboardRoute } from "@/routes/_app/_auth/dashboard/_layout.index";
import { safeRedirectPath } from "@/utils/safeRedirectPath";

export const Route = createFileRoute("/_app/login/_layout/")({
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: safeRedirectPath(search.redirect),
  }),
  component: Login,
});

/**
 * Login page. Once authenticated, sends the user to the validated
 * `redirect` search param, otherwise to onboarding or the dashboard.
 */
function Login() {
  const { isAuthenticated, isLoading: isConvexLoading } = useConvexAuth();
  const { signIn, signUp, isLoading: isAuthKitLoading } = useAuth();
  // Only fetch user data when authenticated (avoids unauthenticated query errors)
  const { data: user } = useQuery({
    ...convexQuery(api.users.queries.getCurrentUser, {}),
    enabled: isAuthenticated,
  });
  const navigate = useNavigate();
  const router = useRouter();
  const { redirect } = Route.useSearch();

  // Redirect authenticated users to the appropriate page
  useEffect(() => {
    if (isConvexLoading || !isAuthenticated || !user) return;

    if (!user.customerId) {
      navigate({ to: OnboardingOrgRoute.fullPath });
      return;
    }
    // Re-validated at the point of use: `redirect` is untrusted input.
    const returnTo = safeRedirectPath(redirect);
    if (returnTo) {
      router.history.replace(returnTo);
      return;
    }
    navigate({ to: DashboardRoute.fullPath });
  }, [isConvexLoading, isAuthenticated, user, navigate, router, redirect]);

  // Show loading while auth state is being determined
  if (isConvexLoading || isAuthKitLoading) {
    return (
      <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary/60" />
      </div>
    );
  }

  // If authenticated, show nothing while redirect happens
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="mx-auto flex h-full w-full max-w-96 flex-col items-center justify-center gap-6">
      <div className="mb-2 flex flex-col gap-2">
        <h3 className="text-center text-2xl font-medium text-primary">
          Continue to Morgan
        </h3>
        <p className="text-center text-base font-normal text-primary/60">
          Sign in to your workspace. If you have been invited, choose Create
          Account.
        </p>
      </div>

      <div className="flex w-full flex-col gap-3">
        <Button className="w-full" onClick={() => signIn()}>
          Sign In
        </Button>

        <Button
          variant="outline"
          className="w-full bg-transparent"
          onClick={() => signUp()}
        >
          Create Account
        </Button>
      </div>

      <p className="px-12 text-center text-sm font-normal leading-normal text-primary/60">
        By clicking continue, you agree to our{" "}
        <Link
          to="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-primary"
        >
          Terms and Conditions
        </Link>{" "}
        and{" "}
        <Link
          to="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-primary"
        >
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
}
